import request from '~/utils/request';
import { appendSortParams } from '~/utils/sort-params';
import {
  GET_PRODUCTS,
  GET_ALL_TEMPLATES,
  CLEAR_FIELDS,
  TEMPLATE_PAGINATION_CHANGE,
  TEMPLATE_SEARCH,
  CLEAR_ALL_TEMPLATE,
  GET_DYNAMIC_FIELDS_FROM_SERVER,
  SET_PRODUCT_DETAILS,
} from './action-types';
import { ENGAGEMENT_TYPES } from '~/utils/constants';

/**
 * Retrieves a list of templates from a server using an HTTP GET request.
 *
 * @param {number} [page=1] - The page number of the templates to retrieve.
 * @param {number} [pageSize=10] - The number of templates per page.
 * @returns {Promise<void>} - A promise that resolves when the action is dispatched.
 */
const getAllTemplates =
  (
    page = 1,
    pageSize = 10,
    search = '',
    productTypes = '',
    creator = '',
    templateType = '',
    productIds = [],
    refresh = true,
    isShared = false,
    includeNumberOfOrders = false,
    sortBy = '',
    sortOrder = ''
  ) =>
  async (dispatch) => {
    try {
      dispatch({
        type: TEMPLATE_PAGINATION_CHANGE,
        payload: { data: { page, pageSize, loading: true } },
      });
      const query = appendSortParams(
        {
          page,
          pageSize,
          search,
          creator,
          templateType,
          productIds,
          isShared,
          productTypes,
          includeNumberOfOrders,
        },
        { sortBy, sortOrder }
      );
      const { data } = await request.get('templates', query);
      dispatch({
        type: GET_ALL_TEMPLATES,
        payload: { data: data.data, refresh },
      });
    } catch (error) {
      return error.response;
    } finally {
      dispatch({
        type: TEMPLATE_PAGINATION_CHANGE,
        payload: { data: { page, pageSize, loading: false } },
      });
    }
  };

/**
 * Makes an HTTP GET request to the 'templates' endpoint and dispatches an action with the retrieved data.
 * @param {number} id - The ID of the template to retrieve.
 * @param {function} dispatch - A function used to dispatch actions to the Redux store.
 * @returns {void}
 */
const getOneTemplate = async (id) => {
  try {
    const response = await request.get(`templates/${id}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Deletes a template by its ID.
 *
 * @param {string} id - The unique identifier of the template to delete.
 * @returns {Promise} A Promise that resolves to the response indicating
 *                    the success of the template deletion, or rejects
 *                    with an error response in case of failure.
 *
 * @throws {Error} If the request to delete the template fails.
 */
const deleteTemplate = async (id) => {
  try {
    const response = await request.delete(`templates/${id}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Retrieves the view proof for a template with the given ID.
 *
 * @param {string} id - The ID of the template for which the view proof is requested.
 * @returns {Promise<object>} - The response object from the GET request, containing the view proof for the template with the given ID.
 * @throws {object} - The error response if there is an error.
 */
const getViewProof = async (id, type = 'json') => {
  try {
    let isHtml = type === 'html' ? true : false;
    const response = isHtml
      ? await request.get(`templates/${id}/view-proof-html`)
      : await request.get(`templates/${id}/view-proof`);
    return response;
  } catch (error) {
    return error.response;
  }
};

const acceptTemplateProof = async (id, payload) => {
  try {
    const response = await request.post(`custom-template-queries/${id}/approve`, payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

const denyTemplateProof = async (id, payload) => {
  try {
    const response = await request.post(`custom-template-queries/${id}/deny`, payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

const getEnvelopeProof = async (payload) => {
  try {
    const response = await request.post(`products/download-envelope-proof`, payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

const getEnvelopeProofForAdmin = async (payload) => {
  try {
    const response = await request.post(`admin/products/download-envelope-proof`, payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

const getViewProofForOrder = async (payload, type = 'json') => {
  try {
    let isHtml = type === 'html' ? true : false;
    const response = isHtml
      ? await request.post(`orders/view-proof-html`, payload)
      : await request.post('orders/view-proof', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

const getViewProofForAdminOrder = async (payload) => {
  try {
    const response = await request.post('admin/orders/view-proof', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Duplicates a template by its ID.
 *
 * @param {string} id - The unique identifier of the template to duplicate.
 * @param {Object} data - Additional data to be sent with the duplicate request.
 * @returns {Promise} A Promise that resolves to the response containing information
 *                    about the duplicated template, or rejects with an error response
 *                    in case of failure.
 *
 * @throws {Error} If the request to duplicate the template fails.
 */

const doublicateTemplate = async (id, data) => {
  try {
    const response = await request.post(`templates/${id}/duplicate`, data);
    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Fetches products and dispatches an action with the product data to the Redux store.
 *
 * @param {Function} dispatch - The dispatch function from the Redux store.
 * @returns {Promise} A Promise that resolves after dispatching the action,
 *                    or rejects if there is an error fetching the products.
 *
 * @throws {Error} If there is an error fetching the products.
 */
const getAllProducts = () => async (dispatch) => {
  try {
    const response = await request.get('/products/types');
    dispatch({
      type: GET_PRODUCTS,
      payload: { products: response.data.data },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

const getAllCustomFields = () => async (dispatch) => {
  try {
    const response = await request.get('custom-fields');
    if (response.status === 200) {
      const data = response.data.data.reduce((acc, curr) => {
        acc[curr.key.replace(/{{|}}/g, '')] = curr;
        return acc;
      }, {});

      dispatch({ type: GET_DYNAMIC_FIELDS_FROM_SERVER, payload: { data } });
    }
  } catch (error) {
    return error.response;
  }
};

const getProductDetails = (payload) => async (dispatch) => {
  try {
    const response = await request.post('/products/template/details', payload);
    if (response.status === 200) {
      dispatch({ type: SET_PRODUCT_DETAILS, payload: response.data.data });
    }
  } catch (error) {
    // Don't leave the previous template's options in place when the request fails
    dispatch({ type: SET_PRODUCT_DETAILS, payload: [] });
    return error.response;
  }
};

const getAllTemplateCategories = async () => {
  try {
    const response = await request.get('templates/categories');
    return response;
  } catch (error) {
    return error.response;
  }
};

const getTemplatesByTab = async (payload) => {
  try {
    const response = await request.post('/templates/by-tab', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

const fetchAllBrandingImages = async (payload) => {
  try {
    const response = await request.get(
      `branding-assets?page=${payload.page}&pageSize=${payload.pageSize}`
    );
    return response;
  } catch (error) {
    return error.response;
  }
};

const deleteImageFromBranding = async (id) => {
  try {
    const response = await request.delete(`branding-assets/${id}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

const uploadImageToBranding = async (formData) => {
  try {
    const response = await request.post(`branding-assets`, formData);
    return response;
  } catch (error) {
    return error.response;
  }
};

const fetchAllQR = async (payload) => {
  try {
    const queryParams = new URLSearchParams({
      page: payload.page,
      pageSize: payload.pageSize,
      engagementType: payload.engagementType || ENGAGEMENT_TYPES.QR_CODE,
    });

    if (payload.customTemplateId) {
      queryParams.set('customTemplateId', payload.customTemplateId);
    }

    const response = await request.get(`qr-code-instances?${queryParams.toString()}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

const deleteQR = async (payload) => {
  try {
    const hasPayload = payload && typeof payload === 'object';
    const id = hasPayload ? payload.id : payload;
    const customTemplateId = hasPayload ? payload.customTemplateId : undefined;
    const response = customTemplateId
      ? await request.delete(`qr-code-instances/${id}`, undefined, { data: { customTemplateId } })
      : await request.delete(`qr-code-instances/${id}`);
    return response;
  } catch (error) {
    return error.response;
  }
};

const updateQRStatus = async (payload) => {
  try {
    const hasPayload = payload && typeof payload === 'object';
    const id = hasPayload ? payload.id : payload;
    const status = hasPayload ? payload.status : undefined;
    const customTemplateId = hasPayload ? payload.customTemplateId : undefined;
    const requestPayload = {
      status,
      ...(customTemplateId ? { customTemplateId } : {}),
    };
    const response = await request.patch(`qr-code-instances/${id}/status`, requestPayload);
    return response;
  } catch (error) {
    return error.response;
  }
};

const uploadQR = async (payload) => {
  try {
    const response = await request.post(`qr-code-instances`, {
      engagementType: ENGAGEMENT_TYPES.QR_CODE,
      ...payload,
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

const updateQR = async (payload) => {
  try {
    const response = await request.put(`qr-code-instances/${payload.id}`, {
      engagementType: ENGAGEMENT_TYPES.QR_CODE,
      ...payload,
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Uploads a template using the provided template form data.
 *
 * @param {FormData} - The form data containing the template files to upload.
 * @returns {Promise} - A promise that resolves with the response from the server.
 * @throws {Error} - If an error occurs during the upload process.
 */
const uploadTemplate = async (formData) => {
  try {
    const response = await request.post('templates/upload', formData);
    const uploadData = response.data?.data || {};

    return uploadData;
  } catch (error) {
    throw normalizeTemplateError(error);
  }
};

const normalizeTemplateError = (error) => {
  if (error?.response) {
    return error.response;
  }

  if (error?.data) {
    return error;
  }

  return { data: { message: error?.message || 'Internal Server Error' } };
};

/**
 * Uploads a single image (field name "image") for insertion into a v2 HTML
 * template - not a whole template design. Same endpoint the Polotno Uploads
 * panel uses (POST /templates/uploadFile), which isn't coupled to Polotno
 * JSON at all - it just writes the file to S3 and returns a public URL.
 * @param {FormData} formData - must contain an "image" field
 * @returns {Promise<string|null>} the uploaded image's public URL, or null on failure
 */
const uploadTemplateImage = async (formData) => {
  try {
    const response = await request.post('templates/uploadFile', formData);
    return response?.data?.data?.filePath || null;
  } catch (error) {
    return null;
  }
};

const isHtmlV2TemplatePayload = (payload) => payload.templateType === 'html_v2';

const assertUploadedTemplateIsReady = (payload, uploadedTemplate) => {
  if (!uploadedTemplate?.templatePath) {
    throw new Error('Template upload failed. Please try again.');
  }

  if (!isHtmlV2TemplatePayload(payload)) {
    return;
  }

  if (!uploadedTemplate.thumbnailPath) {
    throw new Error('Front thumbnail generation failed. Please try again.');
  }

  if (payload.backHtml && !uploadedTemplate.backThumbnailPath) {
    throw new Error('Back thumbnail generation failed. Please try again.');
  }
};

/**
 * Creates a template using the provided data.
 *
 * @param {object} data - The data needed to create the template.
 * @returns {Promise<object>} - A promise that resolves to the response from the successful request or the error response if an error occurs.
 */
const createTemplate = async (formData) => {
  try {
    const payload = {};
    for (const pair of formData.entries()) {
      let value;
      try {
        value = JSON.parse(pair[1]);
      } catch (e) {
        value = pair[1]; // If it's not JSON, use the value as is
      }
      let key = pair[0];
      payload[key] = value;
    }

    const uploadedTemplate = await uploadTemplate(formData);
    assertUploadedTemplateIsReady(payload, uploadedTemplate);

    const templatePayload = {
      title: payload.title,
      productId: payload.productId,
      fields: payload.fields || [],
      thumbnailPath: uploadedTemplate.thumbnailPath || '',
      templatePath: uploadedTemplate.templatePath,
      backThumbnailPath: uploadedTemplate.backThumbnailPath || '',
      additionalInfo: payload.additionalInfo,
      ...(uploadedTemplate.backTemplatePath && { backTemplatePath: uploadedTemplate.backTemplatePath }),
      ...(payload.envelopeType && { envelopeType: payload.envelopeType }),
      // Without this, templateType is silently dropped and the server falls
      // back to file-extension auto-detection - which can never resolve to
      // 'html_v2' (there's no ".html_v2" file extension), so v2 saves would
      // silently land as the legacy 'html' type instead.
      ...(payload.templateType && { templateType: payload.templateType }),
    };

    const response = await request.post('templates', templatePayload);
    return response;
  } catch (error) {
    throw normalizeTemplateError(error);
  }
};

/**
 * Update a template using the provided data.
 * @param {string} id - The id of the template
 * @param {object} data - The data needed to update the template.
 * @returns {Promise<object>} - A promise that resolves to the response from the successful request or the error response if an error occurs.
 */
const updateTemplate = async (id, formData) => {
  try {
    const payload = {};
    for (const pair of formData.entries()) {
      let value;
      try {
        value = JSON.parse(pair[1]);
      } catch (e) {
        value = pair[1]; // If it's not JSON, use the value as is
      }
      let key = pair[0];
      payload[key] = value;
    }

    const uploadedTemplate = await uploadTemplate(formData);
    assertUploadedTemplateIsReady(payload, uploadedTemplate);
    const templatePayload = {
      title: payload.title,
      fields: payload.fields || [],
      thumbnailPath: uploadedTemplate.thumbnailPath || '',
      templatePath: uploadedTemplate.templatePath,
      backThumbnailPath: uploadedTemplate.backThumbnailPath || '',
      additionalInfo: payload.additionalInfo,
      ...(uploadedTemplate.backTemplatePath && { backTemplatePath: uploadedTemplate.backTemplatePath }),
      ...(payload.templateType && { templateType: payload.templateType }),
    };

    const response = await request.patch(`templates/${id}`, templatePayload);
    return response;
  } catch (error) {
    throw normalizeTemplateError(error);
  }
};

const clearAllTemplates = () => (dispatch) => {
  dispatch({ type: CLEAR_ALL_TEMPLATE });
};

/**
 * Validates an unsaved v2 HTML template draft against the same rules the
 * server enforces on save: @page sizing, at least one merge field and one
 * Handlebars conditional/loop, and a real test-compile. Called before the
 * upload+create/update round-trip so validation errors surface without a
 * wasted file upload.
 * @param {{html: string, fields: Array<object>}} payload
 * @returns {Promise<object>} - `{data}` on success, or the error response
 * (422 with `{errors, warnings}` in `data.data`) on failure - callers should
 * check `response.status` rather than assuming success.
 */
const preflightHtmlV2Template = async (payload) => {
  try {
    const response = await request.post('templates/html-v2/preflight', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Renders a proof PDF from unsaved v2 HTML template draft content - no
 * saved Template record required. Used by the editor's "Preview PDF"
 * action, and internally re-runs preflight server-side first.
 * @param {{html: string, productId: number, fields: Array<object>}} payload
 * @returns {Promise<object>}
 */
const viewProofHtmlV2Template = async (payload) => {
  try {
    const response = await request.post('templates/html-v2/view-proof', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Renders front/back proof images from unsaved v2 HTML template draft
 * content. Same payload as viewProofHtmlV2Template, but the server returns
 * base64 images for in-editor preview instead of a PDF download.
 * @param {{html: string, backHtml?: string, productId: number, fields: Array<object>}} payload
 * @returns {Promise<object>}
 */
const generateProofImagesHtmlV2Template = async (payload) => {
  try {
    const response = await request.post('templates/html-v2/generate-proof', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

const searchAndAdvanceChange = (name, value) => (dispatch) => {
  dispatch({ type: TEMPLATE_SEARCH, payload: { name, value } });
};

const clearFilter = () => (dispatch) => dispatch({ type: CLEAR_FIELDS });

export {
  getAllProducts,
  getAllTemplates,
  getOneTemplate,
  clearFilter,
  deleteTemplate,
  getViewProof,
  acceptTemplateProof,
  denyTemplateProof,
  getEnvelopeProof,
  getEnvelopeProofForAdmin,
  doublicateTemplate,
  searchAndAdvanceChange,
  clearAllTemplates,
  getAllCustomFields,
  getProductDetails,
  getViewProofForOrder,
  getViewProofForAdminOrder,
  getAllTemplateCategories,
  getTemplatesByTab,
  fetchAllBrandingImages,
  deleteImageFromBranding,
  uploadImageToBranding,
  fetchAllQR,
  deleteQR,
  updateQRStatus,
  uploadQR,
  updateQR,
  createTemplate,
  updateTemplate,
  preflightHtmlV2Template,
  viewProofHtmlV2Template,
  generateProofImagesHtmlV2Template,
  uploadTemplateImage,
};
